import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import {
  Check as CheckIcon,
  PriorityHigh as AlertIcon,
  Schedule as ScheduleIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TimelineEvent, TimelineEvents } from '../eventTimelineModel';
import { shortDateTime } from './mobileText';

/** The events in phone order: limit breaches newest first, then the other
 *  events that happened newest first, then (after "Now") the scheduled and
 *  pending ones. */
export const mobileEventOrder = (
  events: TimelineEvents,
): { alerts: TimelineEvent[]; past: TimelineEvent[]; future: TimelineEvent[] } => {
  const newestFirst = (a: TimelineEvent, b: TimelineEvent) => (b.time ?? 0) - (a.time ?? 0);
  return {
    alerts: events.past.filter((e) => e.status === 'alert').sort(newestFirst),
    past: events.past.filter((e) => e.status !== 'alert').sort(newestFirst),
    future: events.future,
  };
};

const ICONS: Record<TimelineEvent['status'], { Icon: typeof CheckIcon; color: string }> = {
  alert: { Icon: AlertIcon, color: 'error.main' },
  done: { Icon: CheckIcon, color: 'success.main' },
  scheduled: { Icon: ScheduleIcon, color: 'primary.main' },
  pending: { Icon: PendingIcon, color: 'text.disabled' },
};

const EventRow: React.FC<{ event: TimelineEvent; timezoneIana?: string | null }> = ({ event, timezoneIana }) => {
  const { Icon, color } = ICONS[event.status];
  return (
    <Box
      component="li"
      data-testid="event-row"
      data-status={event.status}
      sx={{ display: 'flex', gap: 1, py: 1, borderTop: '1px solid', borderColor: 'divider', minWidth: 0 }}
    >
      <Icon aria-hidden sx={{ fontSize: 18, color, mt: '2px', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, overflowWrap: 'anywhere', color: event.status === 'alert' ? 'error.main' : 'text.primary' }}>
          {event.label}
        </Typography>
        {event.time != null && (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            {shortDateTime(event.time, timezoneIana)}
          </Typography>
        )}
        {event.details.map((line, i) => (
          <Typography key={i} sx={{ fontSize: '0.8125rem', overflowWrap: 'anywhere' }}>
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

interface MobileEventListProps {
  events: TimelineEvents;
  timezoneIana?: string | null;
}

/** One location's events (buildTimelineEvents) for the phone Location tab:
 *  alerts first, a "Now" divider before what is still to come. */
export const MobileEventList: React.FC<MobileEventListProps> = ({ events, timezoneIana }) => {
  const { t } = useTranslation();
  const { alerts, past, future } = mobileEventOrder(events);
  if (alerts.length + past.length + future.length === 0) {
    return (
      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
        {t('mobile.dashboard.location.noEvents', { defaultValue: 'No events yet' })}
      </Typography>
    );
  }
  return (
    <Box component="ul" aria-label={t('mobile.dashboard.location.events', { defaultValue: 'Events' })} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {[...alerts, ...past].map((e) => (
        <EventRow key={e.key} event={e} timezoneIana={timezoneIana} />
      ))}
      {future.length > 0 && (
        <Box component="li" sx={{ py: 0.5 }}>
          <Divider textAlign="left" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main' }}>
            {t('mobile.dashboard.location.now', { defaultValue: 'Now' })}
          </Divider>
        </Box>
      )}
      {future.map((e) => (
        <EventRow key={e.key} event={e} timezoneIana={timezoneIana} />
      ))}
    </Box>
  );
};

export default MobileEventList;
